/**
 * Customer-facing workflow contract (docs/scope/WORKFLOWS_RUNTIME_CONTRACT.md section 5): the
 * `workflow_step` action card (reply buttons / collect data / rating), the `done.workflow` field and the
 * answer a client sends back (`workflow_reply`). Decoded identically by `@sagot/chat`, the widget
 * panel and the mobile app - `contracts/workflow-steps.json` holds the shared fixtures every
 * decoder is tested against.
 */
import { z } from "zod";

/** `caps` token of the chat request: this client renders workflow cards (contract 5.1). */
export const WORKFLOW_CAP = "workflow_steps";
export const WORKFLOW_CAPS: readonly string[] = [WORKFLOW_CAP];

/**
 * Service Desk ticket cards (`contracts/service-desk/service-step-kinds.json` `wire_kinds`): the ticket summary, the static
 * form, the duplicate offer, the receipt and the list of the client's requests. Here they are only recognised (the kind, the
 * `ticket` block kept as it came); `@sagot/chat/service` decodes the block in full, in the widget's lazy `service` chunk.
 */
export const SERVICE_STEP_KINDS = ["ticket_summary", "ticket_form", "ticket_duplicate", "ticket_receipt", "my_tickets"] as const;
export type ServiceStepKind = (typeof SERVICE_STEP_KINDS)[number];

/** `caps` token of a client that draws the ticket cards (contract 5.1); the intake hands a client without it to a person. */
export const SERVICE_CAP = "service_steps";
/** What a client that draws workflow cards and the ticket cards sends as `caps`. */
export const SERVICE_CAPS: readonly string[] = [WORKFLOW_CAP, SERVICE_CAP];

/** Reserved button ids of the answer-first path (WD-20): "That solved it" / "I still need help" are plain reply buttons with these ids. */
export const ANSWER_FIRST_BUTTONS = ["solved", "still_need_help"] as const;

export const WORKFLOW_STEP_KINDS = ["reply_buttons", "collect_data", "collect_reply", "rating", "verify_email", "article", ...SERVICE_STEP_KINDS] as const;
export type WorkflowStepKind = (typeof WORKFLOW_STEP_KINDS)[number];

/** `ask_rating` scales: five stars (value "1".."5") or a thumb (value "up" | "down"). */
export const WORKFLOW_RATING_SCALES = ["stars", "thumbs"] as const;
export type WorkflowRatingScale = (typeof WORKFLOW_RATING_SCALES)[number];
export const WORKFLOW_RATING_STARS = 5;
export const WORKFLOW_RATING_THUMBS = ["up", "down"] as const;
export type WorkflowRatingThumb = (typeof WORKFLOW_RATING_THUMBS)[number];
/** Longest optional comment that may accompany a rating. */
export const WORKFLOW_COMMENT_MAX_CHARS = 500;

/**
 * Field types: the first six are the data a `collect_data` card asks for; `any` and `digits` are the
 * extra formats of a `collect_reply` question (answered in the composer, no field on the card).
 */
export const WORKFLOW_FIELD_TYPES = ["text", "email", "phone", "number", "date", "dropdown", "any", "digits"] as const;
export type WorkflowFieldType = (typeof WORKFLOW_FIELD_TYPES)[number];

/**
 * `verify_email` cards have two stages: "send" offers to e-mail the code (a button; the request carries the bot check),
 * "enter" waits for the code (the composer takes it, the card offers "Resend code" once). An unknown stage drops the card.
 */
export const WORKFLOW_VERIFY_STAGES = ["send", "enter"] as const;
export type WorkflowVerifyStage = (typeof WORKFLOW_VERIFY_STAGES)[number];
/** Digits of a code when the server does not say (it is the same six as the end-user sign-in code). */
export const WORKFLOW_CODE_LENGTH = 6;
/** Ids of the two buttons that make the server e-mail a code. */
export const WORKFLOW_SEND_CODE_BUTTONS = ["send_code", "resend_code"] as const;

/** What the chat composer does while a workflow run is waiting (`composer` of a card and of `done.workflow`). */
export const WORKFLOW_COMPOSERS = ["open", "locked", "reply"] as const;
export type WorkflowComposer = (typeof WORKFLOW_COMPOSERS)[number];

export const WORKFLOW_CARD_STATES = ["open", "answered"] as const;
export type WorkflowCardState = (typeof WORKFLOW_CARD_STATES)[number];

/** Triggers a client may start without a conversation (`POST /public/v1/workflow-runs`). */
export const WORKFLOW_TRIGGERS = ["widget_opened", "home_option"] as const;
export type WorkflowTrigger = (typeof WORKFLOW_TRIGGERS)[number];

/** `payload.error` when the server re-sends a card after an invalid value. */
export const WORKFLOW_ERROR_INVALID = "invalid";
/** Longest value a text answer may carry (it is also the visible chat message). */
export const WORKFLOW_VALUE_MAX_CHARS = 500;
/** Visible message of a skipped optional field. */
export const WORKFLOW_SKIP_TEXT = "Skip";

/** A privacy notice link is only ever followed when it is a plain https URL (the server validates the same). */
const HTTPS_URL = /^https:\/\/\S+$/i;

/** Blank text is no text. */
function presentText(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed === "" ? undefined : trimmed;
}

function httpsUrl(value: string | undefined): string | undefined {
  const trimmed = presentText(value);
  return trimmed && HTTPS_URL.test(trimmed) ? trimmed : undefined;
}

/** Optional value the server may send as `null` (Pydantic `None`): null → undefined. */
function nullable<T extends z.ZodTypeAny>(schema: T) {
  return schema.nullish().transform((value): z.output<T> | undefined => value ?? undefined);
}

const workflowOption = z.object({ id: z.string().min(1), label: z.string() });

/** `article` card: a link to a page of the knowledge base. Only a plain https address is ever followed (the server sends the same). */
const workflowArticle = z
  .object({
    title: z.string(),
    url: z.string().transform(httpsUrl),
    text: nullable(z.string()).transform(presentText),
    /** The button's words; the server always sends them (translated), `Open article` stands in only if a card arrives without. */
    label: nullable(z.string()).transform(presentText),
  })
  .refine((article) => article.url !== undefined && presentText(article.title) !== undefined)
  .transform((article) => ({ title: article.title.trim(), url: article.url ?? "", text: article.text, label: article.label }));
export type WorkflowArticle = z.output<typeof workflowArticle>;
export type WorkflowOption = z.output<typeof workflowOption>;

/** Missing, unknown or null → `open`. */
const composerSchema = z.enum(WORKFLOW_COMPOSERS).catch("open");

const workflowField = z.object({
  type: z.enum(WORKFLOW_FIELD_TYPES).catch("text"),
  label: z.string(),
  required: z.boolean().catch(true),
  options: nullable(z.array(workflowOption).catch([])),
  /** `collect_reply`: the length the answer must have. */
  min_length: nullable(z.number().int().positive().catch(1)),
  max_length: nullable(z.number().int().positive().catch(WORKFLOW_VALUE_MAX_CHARS)),
});
export type WorkflowField = z.output<typeof workflowField>;

const workflowOtp = z.object({
  stage: z.enum(WORKFLOW_VERIFY_STAGES),
  /** The address as the server masks it ("l•••@example.com"); the full address never travels. */
  email_masked: z.string().catch(""),
  length: z.number().int().positive().catch(WORKFLOW_CODE_LENGTH),
  /** Codes sent so far (a card of an earlier code is no longer the open one). */
  sent: z.number().int().min(0).catch(0),
  /** When the code stops working. */
  expires_at: nullable(z.string()),
  /** When "Resend code" opens. */
  resend_at: nullable(z.string()),
  /** Wrong codes still allowed. */
  attempts_left: nullable(z.number().int().min(0).catch(0)),
});
export type WorkflowOtp = z.output<typeof workflowOtp>;

/** What the visitor answered (restored transcripts; the live card gets it from the client itself). */
export interface WorkflowAnswer {
  button_id?: string;
  value?: string;
  /** The visible text: the button label or the entered value. */
  label?: string;
}

const workflowAnswer = z
  .union([z.string().transform((label) => ({ label })), z.object({ button_id: nullable(z.string()), value: nullable(z.string()), label: nullable(z.string()) })])
  .transform((answer): WorkflowAnswer => answer);

const cardState = z.enum(WORKFLOW_CARD_STATES).catch("open");

/** Reply buttons need buttons, collect data needs a field; a rating and a reply (the composer holds the input) need neither. */
function hasContent(card: { kind: WorkflowStepKind; buttons: readonly unknown[]; field?: unknown; otp?: unknown; article?: unknown; ticket?: unknown }): boolean {
  switch (card.kind) {
    case "reply_buttons":
      return card.buttons.length > 0;
    case "collect_data":
      return card.field !== undefined;
    case "collect_reply":
    case "rating":
      return true;
    case "verify_email":
      return card.otp !== undefined;
    case "article":
      return card.article !== undefined;
    case "ticket_summary":
    case "ticket_form":
    case "ticket_duplicate":
    case "ticket_receipt":
    case "my_tickets":
      return card.ticket !== undefined;
  }
}

export const workflowStepPayload = z
  .object({
    run_id: z.string().min(1),
    step_id: z.string().min(1),
    kind: z.enum(WORKFLOW_STEP_KINDS),
    buttons: z
      .array(workflowOption)
      .nullish()
      .catch([])
      .transform((buttons) => buttons ?? []),
    field: nullable(workflowField),
    allow_typing: z.boolean().catch(true),
    /** `rating`: stars or thumbs (unknown / missing → stars). */
    scale: z.enum(WORKFLOW_RATING_SCALES).catch("stars"),
    /** `rating`: also offer an optional comment (missing → false). */
    ask_comment: z.boolean().catch(false),
    error: z
      .literal(WORKFLOW_ERROR_INVALID)
      .nullish()
      .catch(null)
      .transform((error) => error ?? null),
    /** What the composer does while this card is open (missing / unknown → `open`). */
    composer: composerSchema,
    expires_at: nullable(z.string()),
    /** `collect_data` that asks for personal data: the RA 10173 notice shown with the field (blank / missing → no notice). */
    consent_text: nullable(z.string()).transform(presentText),
    /** Link to the full privacy notice; anything but an https URL is dropped. */
    privacy_url: nullable(z.string()).transform(httpsUrl),
    /** `verify_email`: the stage, the masked address, the digits, the code's expiry and the tries left (required on that kind). */
    otp: nullable(workflowOtp),
    /** `article`: the title, the https link, an optional text and the button's label (required on that kind). */
    article: nullable(workflowArticle),
    /** Ticket cards: the `ticket` block as it came (required on those kinds); `@sagot/chat/service` decodes it with fallbacks. */
    ticket: nullable(z.record(z.unknown())),
    /** Present on restored cards (`GET /public/v1/conversations/{id}`); live cards are `open`. */
    state: cardState.optional().transform((state) => state ?? "open"),
    answer: nullable(workflowAnswer.catch({})),
  })
  .refine(hasContent);
export type WorkflowStepPayload = z.output<typeof workflowStepPayload>;

const SERVICE_KINDS: ReadonlySet<string> = new Set(SERVICE_STEP_KINDS);

/** One of the ticket cards (the `service` chunk of the widget draws them; a client without them draws nothing). */
export function isServiceStepKind(kind: string): kind is ServiceStepKind {
  return SERVICE_KINDS.has(kind);
}

/** A card the widget's lazy `service` chunk draws: a ticket card, or the answer-first reply buttons ("That solved it" / "I still need help"). */
export function isServiceCard(card: Pick<WorkflowStepPayload, "kind" | "buttons">): boolean {
  return isServiceStepKind(card.kind) || (card.kind === "reply_buttons" && card.buttons.some((button) => (ANSWER_FIRST_BUTTONS as readonly string[]).includes(button.id)));
}

/** `workflow_reply` of the chat request (contract 5.1). */
export interface WorkflowReply {
  run_id: string;
  step_id: string;
  /** `reply_buttons`. */
  button_id?: string;
  /** `collect_data`: the value; a dropdown sends the option id, a number a string. `rating`: "1".."5" or "up" | "down". */
  value?: string;
  /** `rating` with `ask_comment`: the visitor's optional note (trimmed, at most 500 characters; omitted when empty). */
  comment?: string;
  /** Ticket summary / form `submit`: what the card held (title, description, answers, guest details); nothing is created before it. */
  ticket?: WorkflowTicketValues;
}

/** What a custom field of a ticket may hold: text, a flag, a number, a list of option ids, or nothing. */
export type WorkflowTicketAnswer = string | number | boolean | string[] | null;

/** The values of a ticket card at `submit` (`workflow_reply.ticket`): bounded text only; priority, team and the like are never the client's to set. */
export interface WorkflowTicketValues {
  title?: string;
  description?: string;
  fields?: Record<string, WorkflowTicketAnswer>;
  guest?: { name: string; email: string; mobile?: string };
}

/** What the visitor picked or typed on a card (`comment` only goes with a rating on a card that asks for one). */
export type WorkflowChoice = { button_id: string; ticket?: WorkflowTicketValues } | { value: string; comment?: string };

/** `done.workflow` and `POST /public/v1/workflow-runs` → `run`; `composer` is what the run asks of the composer after this turn (missing → `open`). */
export const workflowRunRef = z.object({ run_id: z.string().min(1), status: z.string(), composer: composerSchema });
export type WorkflowRunRef = z.output<typeof workflowRunRef>;
